import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Alert, Button, EmptyState, Modal, Select, Spinner } from '@opendate-app/setlist';
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const errorMessage = (err, fallback) => err?.response?.data?.errors?.join(', ') || fallback;

const SectionOfferTiersModal = ({ open, onClose, onSaved, url, csrfToken }) => {
  const [data, setData] = useState(null);
  const [links, setLinks] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    setData(null);
    axios.get(url)
      .then(({ data: rows }) => {
        if (ignore) return;
        setLinks(Object.fromEntries(rows.sections.map(section => [section.key, section.matched ? null : section.tier])));
        setData(rows);
      })
      .catch((err) => {
        if (ignore) return;
        toastError(errorMessage(err, 'There was an error loading the offer tiers.'));
        onClose();
      });
    return () => { ignore = true; };
  }, [open, url]);

  const save = () => {
    setSaving(true);
    axios.patch(url, { section_offer_tiers: links }, { headers: { 'X-CSRF-TOKEN': csrfToken } })
      .then(({ data: saved }) => {
        toastSuccess(saved.repriced > 0 ? 'Section prices updated.' : 'Links saved.');
        onSaved();
      })
      .catch((err) => toastError(errorMessage(err, 'There was an error saving the offer tiers.')))
      .finally(() => setSaving(false));
  };

  const options = (data?.tiers || []).map(tier => ({ value: tier.name, label: `${tier.name} · $${Number(tier.price).toFixed(2)}` }));
  const empty = data && (data.sections.length === 0 || data.tiers.length === 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Link offer tiers"
      description="Pick the offer ticket tier each seat map section should start at. Sections named like a tier fill in on their own; leave one blank to keep its current price."
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!data || empty || data.selling || saving}>Save</Button>
      </>}
    >
      {data === null && <Spinner />}
      {empty && <EmptyState title="Nothing to link" description="This show needs a seat map and an offer with paid ticket tiers." />}
      {data?.selling && !empty && <div className="mb-3"><Alert color="info" title="Prices are locked once a show is selling." /></div>}
      {!empty && data?.sections.map(section => (
        <div key={section.key} className="mb-3">
          <Select
            id={`offer-tier-${section.key}`}
            label={section.name}
            placeholder={section.matched ? `${section.tier} (matched by name)` : 'Keep current price'}
            clearable
            disabled={data.selling}
            options={options}
            value={links[section.key]}
            onChange={(value) => setLinks({ ...links, [section.key]: value || null })}
          />
        </div>
      ))}
    </Modal>
  );
};

SectionOfferTiersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  url: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
};

export default SectionOfferTiersModal;
